import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Action, ActionsDataService, IActionType } from '@services/actions-data.service';
import { convertEpochToDateString } from '@functions/functions';
import { MatMenu } from '@angular/material/menu';

@Component({
	selector: 'app-dashboard-action-list',
	templateUrl: './dashboard-action-list.component.html',
	styleUrls: ['./dashboard-action-list.component.scss']
})
export class DashboardActionListComponent implements OnInit, OnDestroy {

	/** noActions */
	@Output() noActions = new EventEmitter<boolean>();

	/** accountID */
	@Input() accountID: string;

	/** selectedAccount */
	@Input() public selectedAccount: IAccount;

	/** OrganisationID */
	@Input() public organisationUID: string;

	/** onTemplateComponent */
	@Input() public onTemplateComponent: boolean = false;

	/** Actions */
	public Actions: Action[] = [];

	/** filteredActions */
	public filteredActions: Action[] = [];

	/** fields */
	public fields: Object = { text: 'AssetName', id: 'ActionID' };

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** actionTypes - values correspond to value in db table */
	public actionTypes: IActionType[] = [];

	/** myActionsOnly */
	public myActionsOnly: boolean = false;

	/** overdueOnly */
	public overdueOnly: boolean = false;

	/** currentUser */
	public currentUser: WebAppUser;

	/** now */
	public now: number = new Date().getTime();

	/** panelData */
	@Input() panelData: any;

	/** creating */
	@Input() creating: boolean;

	/** titleMenu */
	@Input() titleMenu: MatMenu;

	/** relevantAssets */
	public relevantAssets: LevelAsset[] = [];

	/** listboxHeight */
	public listboxHeight: string = "100px";

	/** mainDiv */
	@ViewChild('main') mainDiv: ElementRef;

	/** mainDivOberserver */
	public mainDivOberserver: ResizeObserver;

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService  
	 * @param authenticationService authenticationService 
	 * @param organisationDataService organisationDataService 
	 * @param actionsDataService actionsDataService 
	 * @param router router 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public authenticationService: AuthenticationService,
		public organisationDataService: OrganisationDataService,
		public actionsDataService: ActionsDataService,
		private router: Router,
		public siteSubscriptions: siteSubscriptions,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		if (this.panelData) this.globalFunctions.displaySpinner(true);
		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.actionsDataService.getActionTypes$().pipe(takeUntil(this.finalise), distinctUntilChanged()),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, queryParamMap, currentUser, ActionTypes]) =>
				!!queryParamMap && !!hierarchicalData && !!selectedAccountGUID && !!selectedOrganisationUID && !!currentUser && !!ActionTypes)
		).subscribe({
			next: ([hierarchicalData, queryParamMap, selectedAccountGUID, selectedOrganisationUID, currentUser, ActionTypes]) => {

				this.actionTypes = ActionTypes;

				this.accountID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;

				this.hierarchy = JSON.parse(hierarchicalData);

				this.currentUser = currentUser;

				this.getActions();
				// this.subscribeOrganisationStructure(this.accountID, this.OrganisationUID)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		if (this.panelData) {
			this.relevantAssets = this.panelData.assets;
		}
		// this.setUpObserver();
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * setUpObserver
	 */
	setUpObserver() {
		if (!this.mainDiv) {
			setTimeout(() => {
				this.setUpObserver()
			}, 100);
		} else {
			this.mainDivOberserver = new ResizeObserver(entries => {
				for (const entry of entries) {
					const element = entry.target;
					const titleElement = <HTMLElement>element.getElementsByClassName("title")[0];
					const titleHeight = titleElement.offsetHeight;
					const bannerElement = <HTMLElement>element.getElementsByClassName("banner")[0];
					const bannerHeight = bannerElement.offsetHeight;
					this.listboxHeight = `calc(${(element.clientHeight - (titleHeight + bannerHeight)) * 1}px - 1em - 1lh)`;
				}
			});

			this.mainDivOberserver.observe(this.mainDiv.nativeElement);
		}
	}

	/**
	 * getActions
	 */
	getActions() {
		if (!this.onTemplateComponent) {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.actionsDataService.getActions$(this.accountID, this.organisationUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((assets) => !!assets),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.globalFunctions.displaySpinner(false);
				})
			).subscribe({
				next: (actions: any) => {
					if (this.relevantAssets.length > 0) {
						this.Actions = actions.filter((x: Action) =>
							this.relevantAssets.map((y: LevelAsset) => y.AssetID).includes(x.AssetID) && !x.Completed);
					} else {
						this.Actions = actions.filter((x: Action) =>
							this.flattenAssets(this.hierarchy).map((y) => y.AssetID).includes(x.AssetID) && !x.Completed);
					}
					this.filterActions();
				},
				error: (error) => {
					if (error.status === 404) {
						this.Actions = [];
						this.filteredActions = [];
						if (this.Actions.length == 0) {
							this.noActions.emit(true);
						}
					}
				}
			});
		} else {
			const newAction: Action = {
				ActionID: 0,
				ActionType: 0,
				AssetID: 0,
				AssetName: "Example Asset",
				EventID: 0,
				TimeLineID: 0,
				Timestamp: 0,
				Completed: false,
				Details: "Example Action to show the dashboard Action list. Details of the Action would be shown here.",
				CompletedTimestamp: 0,
				CompletedByUserID: '',
				CreatedByUserSub: "",
				AssignedToUserSub: "",
				WorkOrderReference: "",
				TargetCompletionTimestamp: new Date().getTime()
			}
			this.Actions = [
				newAction,
				newAction,
				newAction
			];
			this.filteredActions = this.Actions;
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * getActionTypeFromValue
	 * @param value value 
	 * @returns 
	 */
	getActionTypeFromValue(value: number): string {
		let result = this.actionTypes.find((x: IActionType) => x.ActionTypeID == value)?.Description;
		if (result) {
			return result;
		} else {
			return "";
		}
	}

	/** goToFunctionalLocation */
	goToFunctionalLocation(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`/${this.accountID}/organisation/${this.organisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`])

		// this.globalFunctions.displaySpinner(true);
		// this.findLocationUID(asset.AssetID).then((location: string) => {
		// 	this.siteSubscriptions.SetSelectedAsset(asset);
		// 	this.globalFunctions.displaySpinner(false);
		// 	this.router.navigate([`/organisations/${this.OrganisationUID}/location/${location}/asset/${asset.AssetID}`])
		// });
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * filterActions
	 */
	filterActions() {
		this.filteredActions = this.Actions.sort((a, b) => (a.TargetCompletionTimestamp ?? 0) - (b.TargetCompletionTimestamp ?? 0));
		if (this.myActionsOnly) {
			this.filteredActions = this.filteredActions.filter((x: Action) => x.AssignedToUserSub == this.currentUser.Sub);
		}
		if (this.overdueOnly) {
			this.filteredActions = this.filteredActions.filter((x: Action) => this.checkIfOverdue(x));
		}
		if (this.filteredActions.length > 0) {
			this.noActions.emit(false);
		} else {
			this.noActions.emit(true);
		}
	}

	/**
	 * checkIfOverdue
	 * @param action action 
	 * @returns 
	 */
	checkIfOverdue(action: Action): boolean {
		let result: boolean = false;
		if (action.TargetCompletionTimestamp != undefined && (action.TargetCompletionTimestamp + (1 * 24 * 60 * 60 * 1000)) < this.now) {
			result = true;
		}
		return result;
	}

	/**
	 * toggleCheckbox
	 * @param event event 
	 * @param overdue overdue 
	 */
	toggleCheckbox(event: any, overdue: boolean) {
		if (overdue) {
			this.overdueOnly = event.checked;
		} else {
			this.myActionsOnly = event.checked;
		}
		this.filterActions();
	}

	/**
	 * convertEpochNoTime
	 * @param epoch epoch 
	 */
	convertEpochNoTime(epoch: number): string {
		let arr = convertEpochToDateString(epoch).split(" ");
		arr.pop();
		let result: string = "";
		arr.forEach(item => {
			result = result.concat(item, " ");
		})
		return result;
	}
}
